import { PartnerDetailsType } from '~/campaigns/types/campaign.types'
import { useGetStripePriceQuery } from '~/subscription/apis/subscription.api'
import { StripePrice } from '~/subscription/types/subscription.types'

export interface PartnerIntervalPrice {
  /** Undefined when the partner does not price this interval — callers fall back to the geo package. */
  base?: number
  licensePerUser: number
  amount?: number
  /** Configured but never returned — this interval falls back to the standard price. */
  isUnavailable: boolean
  /** The trial on the partner price itself; the checkout charges by it, not by the geo package's. */
  trialPeriodDays?: number
  couponName?: string
}

export interface PartnerCustomPrice {
  monthly: PartnerIntervalPrice
  yearly: PartnerIntervalPrice
  currency?: string
  /** A configured partner price has not answered yet — quoting now would show the standard price. */
  isLoading: boolean
}

// Tier 0 is the first seat, tier 1 each additional one.
const toIntervalPrice = (
  price: StripePrice | undefined,
  numberOfLicenses: number,
  isUnavailable: boolean,
  couponName?: string,
): PartnerIntervalPrice => {
  const base = price != null ? ((price.tiers?.[0]?.unit_amount ?? price.unit_amount) || 0) / 100 : undefined
  const licensePerUser = (price?.tiers?.[1]?.unit_amount ?? 0) / 100

  return {
    base,
    licensePerUser,
    amount: base != null ? base + licensePerUser * (numberOfLicenses - 1) : undefined,
    isUnavailable,
    trialPeriodDays: price?.recurring?.trial_period_days,
    couponName,
  }
}

/**
 * A partner's Stripe price for `numberOfLicenses` seats, undiscounted: the checkout applies the coupon,
 * so discounting here too would charge twice. Shared by the campaign page and the onboarding funnel.
 */
export default function usePartnerCustomPrice(
  partnerDetails: PartnerDetailsType | undefined,
  numberOfLicenses: number,
): PartnerCustomPrice {
  const monthlyPriceId = partnerDetails?.customPriceData?.monthlyPriceId
  const yearlyPriceId = partnerDetails?.customPriceData?.yearlyPriceId
  const monthly = useGetStripePriceQuery(
    { priceId: monthlyPriceId, couponCode: partnerDetails?.couponCode?.monthly },
    { skip: !monthlyPriceId },
  )
  const yearly = useGetStripePriceQuery(
    { priceId: yearlyPriceId, couponCode: partnerDetails?.couponCode?.yearly },
    { skip: !yearlyPriceId },
  )

  // A rejected answer ends the wait too, or a failed request leaves the funnel on its skeleton forever.
  const monthlyPending = !!monthlyPriceId && !monthly.isError && !monthly.isSuccess
  const yearlyPending = !!yearlyPriceId && !yearly.isError && !yearly.isSuccess
  const monthlyUnavailable = !!monthlyPriceId && !monthlyPending && monthly.data?.priceDetails == null
  const yearlyUnavailable = !!yearlyPriceId && !yearlyPending && yearly.data?.priceDetails == null

  return {
    monthly: toIntervalPrice(
      monthly.data?.priceDetails,
      numberOfLicenses,
      monthlyUnavailable,
      monthly.data?.couponDetails?.name,
    ),
    yearly: toIntervalPrice(
      yearly.data?.priceDetails,
      numberOfLicenses,
      yearlyUnavailable,
      yearly.data?.couponDetails?.name,
    ),
    // Partner prices carry their own currency; the geo packages only label the standard ones.
    currency: (yearly.data ?? monthly.data)?.priceDetails?.currency?.toUpperCase(),
    isLoading: monthlyPending || yearlyPending,
  }
}
